/**
 * Parses the Greenhouse job sync preference into the small model used by the settings UI.
 *
 * 1. Treat an absent preference as the default that includes all jobs.
 * 2. Accept exactly one selection field from the GraphQL preference union.
 * 3. Reject malformed data before the form receives it.
 * 4. Build and update the hierarchy used by department and office pickers.
 *
 * The dialog consumes this model. The GraphQL operation and its generated types are the next
 * boundary in GreenhouseJobSyncScopePanel.tsx.
 */

import { err, ok, type Result } from 'neverthrow';

/** Keeps the selected Greenhouse scope in the form's typed draft. */
export type JobSyncScopeDraft = { kind: 'ALL_JOBS' } | { kind: 'DEPARTMENTS' | 'OFFICES' | 'JOBS'; ids: string[] };

/** Describes malformed preference data at the GraphQL boundary. */
export type JobSyncScopeParseFailure = {
  type: 'INVALID_JOB_SYNC_SCOPE_PREF';
  reason: string;
};

/** Holds the raw fields needed to place a remote scope option in a tree. */
export type JobSyncScopeHierarchyOption = {
  id: string;
  name: string;
  parentId?: string | null;
};

/** Adds stable display metadata used by the tree picker and its selection rules. */
export type JobSyncScopeTreeOption<TOption extends JobSyncScopeHierarchyOption> = TOption & {
  /** Nesting level, 0 for a root. Drives the row indent. */
  depth: number;
  /** The path from the root to this option, root first, including this option's own ID. */
  ancestorIds: string[];
  /** Every option below this one in depth-first order. Drives the toggle cascade. */
  descendantIds: string[];
};

/** Represents one raw selection object before its IDs are parsed. */
type JobSyncScopeSelection = Record<string, unknown>;

/** Narrows an unknown GraphQL value to a record before reading its fields. */
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/** Parses the selected ID list so the draft never carries non-string IDs. */
const parseIds = (value: unknown): Result<string[], JobSyncScopeParseFailure> => {
  if (!isRecord(value) || !Array.isArray(value.ids) || !value.ids.every((id) => typeof id === 'string')) {
    return err({ type: 'INVALID_JOB_SYNC_SCOPE_PREF', reason: 'Selection ids must be strings.' });
  }
  return ok(value.ids);
};

/** Converts the GraphQL preference union into the form draft. */
export const parseJobSyncScopeDraft = (value: unknown): Result<JobSyncScopeDraft, JobSyncScopeParseFailure> => {
  if (value === null || value === undefined) {
    return ok({ kind: 'ALL_JOBS' });
  }
  if (!isRecord(value) || value.__typename !== 'AtsJobSyncScopePref') {
    return err({ type: 'INVALID_JOB_SYNC_SCOPE_PREF', reason: 'The preference type is not supported.' });
  }

  const selections: Array<{ kind: JobSyncScopeDraft['kind']; value: JobSyncScopeSelection }> = [];
  const departmentSelection = value.departments;
  const officeSelection = value.offices;
  const jobSelection = value.jobs;

  if (departmentSelection !== null && departmentSelection !== undefined) {
    if (!isRecord(departmentSelection)) {
      return err({ type: 'INVALID_JOB_SYNC_SCOPE_PREF', reason: 'Department selection must be an object.' });
    }
    selections.push({ kind: 'DEPARTMENTS', value: departmentSelection });
  }
  if (officeSelection !== null && officeSelection !== undefined) {
    if (!isRecord(officeSelection)) {
      return err({ type: 'INVALID_JOB_SYNC_SCOPE_PREF', reason: 'Office selection must be an object.' });
    }
    selections.push({ kind: 'OFFICES', value: officeSelection });
  }
  if (jobSelection !== null && jobSelection !== undefined) {
    if (!isRecord(jobSelection)) {
      return err({ type: 'INVALID_JOB_SYNC_SCOPE_PREF', reason: 'Job selection must be an object.' });
    }
    selections.push({ kind: 'JOBS', value: jobSelection });
  }

  if (selections.length !== 1) {
    return err({ type: 'INVALID_JOB_SYNC_SCOPE_PREF', reason: 'The preference must contain one selection.' });
  }

  return parseIds(selections[0].value).map((ids) => ({ kind: selections[0].kind, ids }));
};

/**
 * Orders remote scope options for a tree picker and adds the metadata needed to render and toggle
 * each row.
 *
 * 1. Treat missing and unknown parents as roots.
 * 2. Sort roots and siblings by name, without case sensitivity.
 * 3. Walk depth first and record ancestor and descendant IDs in that same order.
 * 4. Start any unvisited cycle member as a root so malformed data cannot prevent rendering.
 */
export const buildJobSyncScopeTree = <TOption extends JobSyncScopeHierarchyOption>(
  options: TOption[]
): JobSyncScopeTreeOption<TOption>[] => {
  const optionById = new Map<string, TOption>();
  for (const option of options) {
    optionById.set(option.id, option);
  }

  const childrenByParentId = new Map<string, TOption[]>();
  const roots: TOption[] = [];
  for (const option of options) {
    const parentId = option.parentId;
    if (!parentId || !optionById.has(parentId)) {
      roots.push(option);
      continue;
    }
    const children = childrenByParentId.get(parentId) ?? [];
    children.push(option);
    childrenByParentId.set(parentId, children);
  }

  const byName = (left: TOption, right: TOption): number =>
    left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
  roots.sort(byName);
  for (const children of childrenByParentId.values()) {
    children.sort(byName);
  }

  const visited = new Set<string>();
  const treeById = new Map<string, JobSyncScopeTreeOption<TOption>>();
  const tree: JobSyncScopeTreeOption<TOption>[] = [];

  const append = (option: TOption, depth: number, parentAncestorIds: string[]): void => {
    if (visited.has(option.id)) {
      return;
    }
    visited.add(option.id);

    const ancestorIds = [...parentAncestorIds, option.id];
    const treeOption: JobSyncScopeTreeOption<TOption> = {
      ...option,
      depth,
      ancestorIds,
      descendantIds: [],
    };
    treeById.set(option.id, treeOption);
    tree.push(treeOption);

    const children = childrenByParentId.get(option.id) ?? [];
    for (const child of children) {
      const wasVisited = visited.has(child.id);
      append(child, depth + 1, ancestorIds);
      const childTree = treeById.get(child.id);
      if (childTree && !wasVisited) {
        treeOption.descendantIds.push(child.id, ...childTree.descendantIds);
      }
    }
  };

  for (const root of roots) {
    append(root, 0, []);
  }

  const unvisited = options.filter((option) => !visited.has(option.id)).sort(byName);
  for (const option of unvisited) {
    append(option, 0, []);
  }

  return tree;
};

/**
 * Filters a scope tree while keeping the ancestors required to render each match in context.
 *
 * A full path label makes a parent match include its subtree. Ancestor IDs then restore the path
 * for a child-only match. The input order remains the depth-first order from buildJobSyncScopeTree.
 */
export const filterJobSyncScopeTree = <TOption extends JobSyncScopeHierarchyOption>(
  tree: readonly JobSyncScopeTreeOption<TOption>[],
  searchText: string,
  getLabel: (option: JobSyncScopeTreeOption<TOption>) => string
): JobSyncScopeTreeOption<TOption>[] => {
  const normalizedSearchText = searchText.trim().toLowerCase();
  if (!normalizedSearchText) {
    return [...tree];
  }

  const keptIds = new Set<string>();
  for (const option of tree) {
    const normalizedLabel = getLabel(option).toLowerCase();
    if (!normalizedLabel.includes(normalizedSearchText)) {
      continue;
    }
    for (const ancestorId of option.ancestorIds) {
      keptIds.add(ancestorId);
    }
  }

  return tree.filter((option) => keptIds.has(option.id));
};

/**
 * Toggles a node and every descendant while preserving the existing ID order.
 *
 * A leaf has no descendants, so its toggle changes only its own ID. An ID that is absent from the
 * tree is a saved entity that Greenhouse no longer returns; it is treated as a leaf so the user can
 * remove its stale chip.
 */
export const toggleJobSyncScopeSelection = <TOption extends JobSyncScopeHierarchyOption>(
  selectedIds: string[],
  toggledId: string,
  tree: readonly JobSyncScopeTreeOption<TOption>[]
): string[] => {
  const toggledOption = tree.find((option) => option.id === toggledId);
  const affectedIds = [toggledId, ...(toggledOption?.descendantIds ?? [])];
  if (selectedIds.includes(toggledId)) {
    return selectedIds.filter((id) => !affectedIds.includes(id));
  }

  const selectedIdSet = new Set(selectedIds);
  const nextSelectedIds = [...selectedIds];
  for (const id of affectedIds) {
    if (!selectedIdSet.has(id)) {
      nextSelectedIds.push(id);
    }
  }
  return nextSelectedIds;
};
